import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Inbox } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import {
  useListingEngagementQuery,
  useListingPackagesQuery,
  useListingTransactionsQuery,
  usePromotedListingQuery,
} from '@/client/api/promotion-management/promoted-listing.query';
import { getApiErrorMessage } from '@/client/utils/errors';
import { ListingHeaderCard } from './PromotedListings/ListingHeaderCard';
import { PackagesPanel } from './PromotedListings/PackagesPanel';
import { PackageEngagementAnalytics } from './PromotedListings/PackageEngagementAnalytics';
import { FinancialRecords } from './PromotedListings/FinancialRecords';

/** The endpoint caps a page at 100; one page covers every real listing's history. */
const TRANSACTION_PAGE_SIZE = 100;
const CURRENT_YEAR = new Date().getFullYear();

/**
 * PROMOTED LISTING — detail page.
 *
 * Four endpoints feed this: the listing rollup, its package history, its
 * promotion transactions, and monthly engagement for the selected year.
 */
const PromotedListingDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { eid } = useParams<{ eid?: string }>();
  const numericEid = eid && /^\d+$/.test(eid) ? Number(eid) : null;

  const [year, setYear] = useState(CURRENT_YEAR);

  const listing = usePromotedListingQuery(numericEid);
  const packages = useListingPackagesQuery(numericEid);
  const transactions = useListingTransactionsQuery(numericEid, {
    page: 1,
    page_size: TRANSACTION_PAGE_SIZE,
  });
  const engagement = useListingEngagementQuery(numericEid, year);

  useBreadcrumbs([
    { label: 'Promotions Management', href: '#' },
    { label: 'Promoted Listings', href: '/admin/promotions' },
    { label: listing.data?.listing.name_basic ?? 'Listing' },
  ]);

  const notFound = !numericEid || (listing.isError && !listing.data);
  const errorMessage = listing.isError
    ? getApiErrorMessage(listing.error, 'Failed to fetch this promoted listing')
    : null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl space-y-5">
        <button
          onClick={() => navigate('/admin/promotions')}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to listings
        </button>

        {listing.isPending && numericEid ? (
          <div className="h-40 animate-pulse rounded-2xl border border-gray-200 bg-white shadow-sm" />
        ) : notFound ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white py-20 text-center shadow-sm">
            <Inbox className="mb-3 h-8 w-8 text-gray-300" />
            <p className="text-sm font-medium text-gray-700">Listing not found</p>
            <p className="mt-1 text-xs text-gray-400">
              {errorMessage ?? `EID ${eid ?? '—'} isn’t in the promoted-listings set.`}
            </p>
            <button
              onClick={() => navigate('/admin/promotions')}
              className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Back to listings
            </button>
          </div>
        ) : (
          listing.data && (
            <div className="space-y-6">
              <ListingHeaderCard listing={listing.data} />

              {packages.isError && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {getApiErrorMessage(packages.error, 'Failed to fetch the package history')}
                </div>
              )}

              <PackageEngagementAnalytics
                series={engagement.data?.series ?? []}
                year={year}
                onYear={setYear}
                maxYear={CURRENT_YEAR}
                isLoading={engagement.isPending}
                isFetching={engagement.isFetching}
              />

              <FinancialRecords
                transactions={transactions.data}
                isLoading={transactions.isPending}
                isFetching={transactions.isFetching}
              />

              <PackagesPanel packages={packages.data ?? []} isLoading={packages.isPending} />
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PromotedListingDetailPage;
